import React from 'react';
import { T, MONO, radius, shadow, type as ty, tint } from './theme';
import { IconTrend } from './icons';

type Div = React.HTMLAttributes<HTMLDivElement>;
const merge = (base: React.CSSProperties, s?: React.CSSProperties) => ({ ...base, ...(s || {}) });

// ── Card ───────────────────────────────────────────────────────
// The base surface for every panel. `hover` lifts on pointer.
export function Card({ children, style, pad = 22, hover = false, accentEdge, ...rest }:
  Div & { pad?: number; hover?: boolean; accentEdge?: string }) {
  const [h, setH] = React.useState(false);
  return (
    <div {...rest}
      onMouseEnter={hover ? () => setH(true) : undefined}
      onMouseLeave={hover ? () => setH(false) : undefined}
      style={merge({
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderLeft: accentEdge ? `3px solid ${accentEdge}` : undefined,
        borderRadius: radius.xxl,
        boxShadow: h ? shadow.hover : shadow.card,
        padding: pad,
        transform: h ? 'translateY(-3px)' : 'none',
        transition: 'transform .18s, box-shadow .18s',
      }, style)}>
      {children}
    </div>
  );
}

// ── Inner tile (nested stat block inside a Card) ───────────────
export function Tile({ children, style, pad = 16, ...rest }: Div & { pad?: number }) {
  return (
    <div {...rest} style={merge({
      background: T.card, border: `1px solid ${T.border}`,
      borderRadius: radius.lg, padding: pad,
    }, style)}>{children}</div>
  );
}

// ── Eyebrow + section header ───────────────────────────────────
export const Eyebrow = ({ children, color = T.textDim }: { children: React.ReactNode; color?: string }) =>
  <div style={{ ...ty.eyebrow, color, marginBottom: 5 }}>{children}</div>;

export function SectionHeader({ eyebrow, title, right }:
  { eyebrow?: string; title: string; right?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
      <div>{eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}<div style={{ ...ty.sectionTitle, color: T.text }}>{title}</div></div>
      {right}
    </div>
  );
}

// ── Label + value (the atomic stat) ────────────────────────────
export function Stat({ label, value, color = T.text, size = 17 }:
  { label: string; value: React.ReactNode; color?: string; size?: number }) {
  return (
    <div>
      <div style={{ ...ty.label, color: T.textDim, marginBottom: 5 }}>{label}</div>
      <div style={{ fontSize: size, fontWeight: 800, color, fontFamily: MONO }}>{value}</div>
    </div>
  );
}

// ── Delta pill (▲ 19.4%) ───────────────────────────────────────
export function DeltaPill({ value, dir = 'up' }: { value: string; dir?: 'up' | 'down' | 'flat' }) {
  const pos = dir !== 'down';
  const c = dir === 'flat' ? T.textDim : pos ? T.greenText : T.redText;
  const bg = dir === 'flat' ? T.card : tint(pos ? T.green : T.red, 13);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, padding: '3px 8px 3px 6px',
      borderRadius: radius.pill, background: bg, color: c, fontSize: 12, fontWeight: 700, fontFamily: MONO }}>
      {dir !== 'flat' && <IconTrend up={pos} />}{value}
    </span>
  );
}

// ── Badge ──────────────────────────────────────────────────────
export function Badge({ children, color = T.accent, solid = false }:
  { children: React.ReactNode; color?: string; solid?: boolean }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
      padding: '4px 10px', borderRadius: radius.pill,
      color: solid ? '#fff' : color, background: solid ? color : tint(color, 12) }}>
      {children}
    </span>
  );
}

// ── Live dot ───────────────────────────────────────────────────
export const LiveDot = ({ color = T.green }: { color?: string }) =>
  <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block',
    animation: 'pdot 2s infinite' }} />;

// ── Icon chip (tinted rounded square holding a line icon) ──────
export function IconChip({ children, color = T.accent, size = 44 }:
  { children: React.ReactNode; color?: string; size?: number }) {
  return (
    <div style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), flexShrink: 0,
      background: tint(color, 14), color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {children}
    </div>
  );
}

// ── Progress bar ───────────────────────────────────────────────
export function Progress({ pct, color = T.accent, track = 'var(--py-track-bg)' }:
  { pct: number; color?: string; track?: string }) {
  return (
    <div style={{ height: 7, borderRadius: radius.pill, background: track, overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(100, pct)}%`,
        background: `linear-gradient(90deg, ${color}, ${tint(color, 40)})`, borderRadius: radius.pill }} />
    </div>
  );
}

// ── Button ─────────────────────────────────────────────────────
export function Button({ children, variant = 'ghost', color = T.accent, style, ...rest }:
  React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'solid' | 'soft' | 'ghost' | 'outline'; color?: string }) {
  const map: Record<string, React.CSSProperties> = {
    solid:   { background: color, color: '#fff', border: 'none' },
    soft:    { background: tint(color, 12), color, border: `1px solid ${tint(color, 35)}` },
    outline: { background: 'var(--py-surface)', color, border: `1px solid ${tint(color, 42)}` },
    ghost:   { background: 'transparent', color: T.textMid, border: `1px solid ${T.borderMid}` },
  };
  return (
    <button {...rest} style={merge({
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7,
      padding: '9px 16px', borderRadius: radius.md, fontFamily: 'inherit',
      fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all .15s', ...map[variant],
    }, style)}>{children}</button>
  );
}

// ── Auto-fit grid (reflows with zero media queries) ────────────
// <Grid min={158}> lays children out edge-to-edge, wrapping as width shrinks.
export function Grid({ min = 240, gap = 14, children, style, ...rest }:
  Div & { min?: number; gap?: number }) {
  return (
    <div {...rest} style={merge({
      display: 'grid', gap, gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
    }, style)}>{children}</div>
  );
}

// ── KPI card (Grid child) ──────────────────────────────────────
export function KpiCard({ label, value, delta, dir = 'up', spark }:
  { label: string; value: React.ReactNode; delta?: string; dir?: 'up'|'down'|'flat'; spark?: React.ReactNode }) {
  return (
    <Card pad={18} hover style={{ borderRadius: radius.xl }}>
      <div style={{ ...ty.eyebrow, letterSpacing: '0.07em', color: T.textDim }}>{label}</div>
      <div style={{ ...ty.kpi, color: T.text, marginTop: 11 }}>{value}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 13 }}>
        {delta ? <DeltaPill value={delta} dir={dir} /> : <span />}
        {spark}
      </div>
    </Card>
  );
}
